import React, { useEffect, useState } from "react";
import { serviceRequestsActions, useDispatch, useSelector } from "../../state";
import { serviceRequestsSelectors } from "../../state/serviceRequests/selectors";
import { authSelectors } from "../../state/auth/selectors";
import { DataGridPro } from "@mui/x-data-grid-pro";
import type { GridColDef } from "@mui/x-data-grid-pro";
import { SERVICE_REQUEST_COLUMNS as columns } from "../../constants/tables";
import { Box, Typography } from "@mui/material";
import { format } from "date-fns";
import { formatPolicyType, formatRequestType } from "../../utils";
import { StatusChip } from "../../components/components";
import { useResponsiveDataGridProps } from "../../hooks/useResponsiveDataGridProps";

export default function ServiceRequestsPage() {
  const dispatch = useDispatch();
  // get role and active agency id
  const role = useSelector(authSelectors.activeRole);
  const activeAgencyId = useSelector(authSelectors.activeAgencyId);
  const serviceRequests = useSelector(serviceRequestsSelectors.serviceRequests);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => {
    dispatch(serviceRequestsActions.getServiceRequests(role, activeAgencyId));
  }, [dispatch, role, activeAgencyId]);

  // Mobile-only "summary" column. Stacks the most useful fields into one
  // wide cell so a narrow phone screen still tells the user everything they
  // need. Hidden on desktop by `useResponsiveDataGridProps` when
  // `hasMobileSummaryColumn` is true.
  const mobileSummaryColumn: GridColDef = {
    field: "mobileSummary",
    headerName: "Service Request",
    flex: 1,
    minWidth: 220,
    sortable: false,
    filterable: false,
    cellClassName: "mobile-summary-cell",
    renderCell: (params) => {
      const row = params.row;
      const name = `${row.first_name || ""} ${row.last_name || ""}`.trim();
      const policy = row.policy_type ? formatPolicyType(row.policy_type) : "";
      const request = row.request_type
        ? formatRequestType(row.request_type)
        : "";
      const typeLine = [policy, request].filter(Boolean).join(" · ");
      const submitted = row.created_at
        ? format(new Date(row.created_at), "MM/dd/yyyy")
        : "";
      const due = row.due_date
        ? format(new Date(row.due_date), "MM/dd/yyyy")
        : "";
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.25,
            py: 1,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.2 }}
            noWrap
          >
            {name || "—"}
          </Typography>
          {row.status && (
            <Box sx={{ display: "flex", mt: 0.25, mb: 0.25 }}>
              <StatusChip status={row.status} />
            </Box>
          )}
          {(row.email || row.phone) && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.3,
              }}
            >
              {row.email}
              {row.phone ? ` · ${row.phone}` : ""}
            </Typography>
          )}
          {typeLine && (
            <Typography
              variant="caption"
              sx={{ color: "#0033ff", fontWeight: 500 }}
            >
              {typeLine}
            </Typography>
          )}
          {(submitted || due) && (
            <Typography variant="caption" color="text.secondary">
              {submitted ? `Submitted ${submitted}` : ""}
              {submitted && due ? " · " : ""}
              {due ? `Due ${due}` : ""}
            </Typography>
          )}
        </Box>
      );
    },
  };

  const allColumns: GridColDef[] = [mobileSummaryColumn, ...columns];

  const responsiveProps = useResponsiveDataGridProps({
    // Pin the identifier when scrolling horizontally on mobile.
    pinFirst: "mobileSummary",
    // Toggles `mobileSummary` between mobile (visible) and desktop (hidden).
    hasMobileSummaryColumn: true,
    // Hide the per-field columns on mobile in favour of the summary cell.
    hideOnMobile: [
      "id",
      "status",
      "agency",
      "name",
      "phone",
      "email",
      "policy_type",
      "request_type",
      "created_at",
      "due_date",
    ],
  });

  return (
    <div>
      <Typography variant="h1" sx={{ fontSize: 20, fontWeight: 600, my: 1 }}>
        Service Requests
      </Typography>
      <DataGridPro
        autoHeight
        rows={serviceRequests}
        columns={allColumns}
        rowsPerPageOptions={[10, 25, 50, 100, 250]}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        pagination
        {...responsiveProps}
      />
    </div>
  );
}
